// src/pages/AdminChatPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { io } from 'socket.io-client';
import { ChatAdminView } from '../components/ChatUI';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import ChatQuickReplies from '../components/ChatQuickReplies';

const API_URL = process.env.REACT_APP_API_URL || '/';

export default function AdminChatPanel() {
  const [rooms, setRooms] = useState([]);
  const [quickRepliesOpen, setQuickRepliesOpen] = useState(false);
  const closeQuickReplies = useCallback(() => setQuickRepliesOpen(false), []);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const selectedRef = useRef(null);
  selectedRef.current = selectedRoom;

  const socketRef = useRef(null);
  const followRef = useRef(false);
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get('/api/chat/admin/rooms', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRooms(res.data.rooms);
      } catch (err) {
        Swal.fire({ icon: 'error', text: 'خطا در دریافت اتاق‌ها', toast: true });
      }
    };
    fetchRooms();
  }, [token]);

  useEffect(() => {
    socketRef.current = io(API_URL, {
      auth: { token },
      transports: ['polling', 'websocket'],
      path: '/socket.io'
    });

    const socket = socketRef.current;


    socket.on('previous_messages', (msgs) => {
      if (selectedRef.current && (!msgs.length || Number(msgs[0].room_id) === Number(selectedRef.current.id))) setMessages(msgs);
    });

    socket.on('receive_message', (msg) => {
      if (msg.room_id && Number(msg.room_id) !== Number(selectedRef.current?.id)) return;
      setMessages(prev => prev.some(item => Number(item.id) === Number(msg.id)) ? prev : [...prev, { ...msg, animate: true }]);
      setIsTyping(false);
    });

    socket.on('typing', () => setIsTyping(true));
    socket.on('stop_typing', () => setIsTyping(false));

    socket.on('connect', () => { if (selectedRef.current) socket.emit('admin_join_room', { room_id: selectedRef.current.id }); setConnected(true); });
    socket.on('chat_error', ({ message }) => Swal.fire({ icon: 'error', text: message, toast: true }));
    socket.on('messages_read', ({ room_id, message_ids }) => {
      if (Number(room_id) !== Number(selectedRef.current?.id)) return;
      setMessages(prev => prev.map(msg => message_ids.map(Number).includes(Number(msg.id)) ? { ...msg, is_read: 1, legacy_read_unknown: 0 } : msg));
    });
    socket.on('chat_room_updated', () => {
      api.get('/api/chat/admin/rooms').then(res => setRooms(res.data.rooms)).catch(() => {});
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('unread_count', ({ room_id }) => setRooms(prev => prev.map(r => r.id === room_id ? { ...r, unread_count: (r.unread_count || 0) + 1 } : r)));
    socket.on('unread_cleared', ({ room_id }) => setRooms(prev => prev.map(r => r.id === room_id ? { ...r, unread_count: 0 } : r)));
    socket.on('unread_count_updated', ({ room_id, count }) => setRooms(prev => prev.map(r => r.id === room_id ? { ...r, unread_count: count } : r)));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [token]);

  const searchUsers = async () => {
    if (!search.trim() || searching) return;
    setSearching(true);
    try {
      const q = search.trim().replace(/[۰-۹٠-٩]/g, digit => '۰۱۲۳۴۵۶۷۸۹'.includes(digit) ? '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit) : '٠١٢٣٤٥٦٧٨٩'.indexOf(digit));
      const res = await api.get('/api/adminCharge/search', { params: { q } });
      setSearchResults(res.data.users || []);
      setSearchDone(true);
    } catch { Swal.fire({ icon: 'error', text: 'جست‌وجوی کاربر انجام نشد' }); }
    finally { setSearching(false); }
  };
  const startUser = async user => {
    if (searching || busy) return;
    setSearching(true);
    try {
      const res = await api.post('/api/chat/admin/start', { user_id: user.id });
      const room = res.data.room;
      setRooms(prev => prev.some(item => Number(item.id) === Number(room.id)) ? prev : [room, ...prev]);
      selectRoom(room);
    } catch { Swal.fire({ icon: 'error', text: 'شروع گفتگو انجام نشد' }); }
    finally { setSearching(false); }
  };

  useEffect(() => {
    if (!message.trim() || !selectedRoom || !socketRef.current) return;

    socketRef.current.emit('typing', { room_id: selectedRoom.id });
    const timeout = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { room_id: selectedRoom.id });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [message, selectedRoom]);

  const sendMessage = async () => {
    if ((!message.trim() && !file) || busy || !selectedRoom || !socketRef.current?.connected) return;
    const socket = socketRef.current;
    setBusy(true);
    try {

      let file_url = null;
      let file_type = null;

      if (file) {
        const form = new FormData();
        form.append('file', file);
        try {
          const res = await api.post('/api/chat/upload', form, {
            headers: { Authorization: `Bearer ${token}` }
          });
          file_url = res.data.file_url;
          file_type = res.data.file_type;
        } catch (err) {
          Swal.fire({ icon: 'error', text: 'خطا در آپلود', toast: true });
          return;
        }
      }

      if (!socket.connected || socketRef.current !== socket) return;
      socket.emit('admin_send_message', {
        room_id: selectedRoom.id,
        message: message.trim(),
        file_url,
        file_type
      });

      setMessage('');
      setFile(null);
      followRef.current = true;
    } finally { setBusy(false); }
  };

  const selectRoom = (room) => {
    if (busy) return;
    if (room.id === selectedRoom?.id) return;
    if (socketRef.current?.connected) {
      if (selectedRoom) socketRef.current.emit('admin_leave_room', { room_id: selectedRoom.id });
      socketRef.current.emit('admin_join_room', { room_id: room.id });
    }
    setMessages([]);
    setMessage('');
    setFile(null);
    setIsTyping(false);
    followRef.current = false;
    setSelectedRoom(room);
  };
  const goBack = () => {
    if (busy) return;
    if (socketRef.current && selectedRoom) socketRef.current.emit('admin_leave_room', { room_id: selectedRoom.id });
    setSelectedRoom(null);
    setMessages([]);
    setMessage('');
    setFile(null);
    setIsTyping(false);
  };
  return <><ChatAdminView rooms={rooms} selectedRoom={selectedRoom} selectRoom={selectRoom} goBack={goBack} navigate={navigate} onManageReplies={() => setQuickRepliesOpen(true)}
    messages={messages} isTyping={isTyping} followRef={followRef} message={message} setMessage={setMessage}
    file={file} setFile={setFile} sendMessage={sendMessage} busy={busy} connected={connected}
    search={search} setSearch={setSearch} searchUsers={searchUsers} searchResults={searchResults} searching={searching} startUser={startUser} searchDone={searchDone}
    onSeen={ids => { if (socketRef.current?.connected && selectedRoom) socketRef.current.emit('read_messages', { room_id: selectedRoom.id, message_ids: ids }); }} />{quickRepliesOpen && <ChatQuickReplies onClose={closeQuickReplies} />}</>;
}
